import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type React from "react";
import { type FC, type MouseEvent, memo } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { SURFACE } from "../../../../theme/tokens";
import type { DepartmentLocation, RecurringShift } from "../../../../utils/models";
import { ClickableShiftCell } from "../../v2/Cells/ClickableShiftCell";
import { addButtonLeadingIconGap, addButtonStyle, type ScheduleDayAtLocationData } from "../../v2/utils";
import { CELL_ITEM_GAP, CELL_ITEM_HEIGHT, CELL_PADDING_Y, CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID } from "../consts";

type Props = {
	day: ScheduleDayAtLocationData;
	cellDepartmentLocation: DepartmentLocation;
	/** True when this cell's column is today — drives the highlight. */
	isToday: boolean;
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	handleOnAddClick: (clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
};

/**
 * One day cell in the consecutive grid. Renders the placed shift chips (reusing the
 * MasterScheduleV2 `ClickableShiftCell`) plus EXACTLY ONE "+" add button — this view never shows
 * per-slot gaps, just a single affordance to add a recurring shift on this cell's date.
 *
 * Not droppable (no drag-and-drop yet). If DnD is added later, wrap the container in a
 * `useDroppable` node and each `ClickableShiftCell` in a draggable — the data model already
 * carries everything needed (see `DraggableShift`/`DroppableCell` in v2).
 */
export const ConsecutiveCell: FC<Props> = memo(({ day, cellDepartmentLocation, isToday, onShiftClick, onShiftKeyDown, handleOnAddClick }) => {
	const { t } = useTranslation();
	const { getNurseById, chosenDepartment } = useHospitalData();
	const isMasterScheduleEditBlocked = chosenDepartment.disable_edit_master_schedule;
	const hasMultipleShifts = day.recurringShiftsInCell.length > 1;

	return (
		<Box
			data-testid={`consecutive-master-schedule-cell-${day.dayDate.toISODate()}-${cellDepartmentLocation.id ?? "no-id"}`}
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				justifyContent: "flex-start",
				gap: `${CELL_ITEM_GAP}px`,
				paddingBlock: `${CELL_PADDING_Y / 2}px`,
				paddingInline: "2px",
				width: "100%",
				height: "100%",
				minWidth: 0,
				position: "relative",
				background: SURFACE,
				...(isToday
					? {
							backgroundColor: alpha(theme.palette.primary.main, 0.06),
							// Top accent bar marking the current day (mirrors the MagicPatterns reference).
							"&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: theme.palette.primary.main },
						}
					: {}),
			})}
		>
			{day.recurringShiftsInCell.map((recurringShiftInCell, index) => (
				// Fixed-height slot so the row-height math (see consts) matches the rendered content exactly.
				<Box key={recurringShiftInCell.recurringShift.id ?? index} sx={{ height: `${CELL_ITEM_HEIGHT}px`, flexShrink: 0, display: "flex", overflow: "hidden" }}>
					<ClickableShiftCell
						nurseId={recurringShiftInCell.recurringShift.nurseId}
						recurringShiftId={recurringShiftInCell.recurringShift.id}
						multipleShifts={hasMultipleShifts}
						onShiftClick={(event) => onShiftClick(recurringShiftInCell.recurringShift, event, day.dayDate, cellDepartmentLocation)}
						onShiftKeyDown={(event) => onShiftKeyDown(recurringShiftInCell.recurringShift, event, day.dayDate, cellDepartmentLocation)}
						isFutureShift={recurringShiftInCell.isFutureShift}
						recurringShiftStartDate={recurringShiftInCell.recurringShift.startDate}
						relevantNurse={getNurseById(recurringShiftInCell.recurringShift.nurseId)}
						timeOffDuringShift={recurringShiftInCell.longPtoForShift}
						referenceDate={day.dayDate}
						isSynergyMS={false}
						isCredentialed={true}
						highlightRootId={CONSECUTIVE_MASTER_SCHEDULE_ROOT_ID}
					/>
				</Box>
			))}
			{/* Fixed-height "+" slot; the inner MUI button (natively 40px) is forced to fill it. */}
			<Box sx={{ height: `${CELL_ITEM_HEIGHT}px`, flexShrink: 0, "& button": { height: "100%", width: "100%", minHeight: 0 } }}>
				<SystemButton
					buttonVariant="borderless"
					sx={addButtonStyle}
					leadingIconGap={addButtonLeadingIconGap}
					onClick={() => handleOnAddClick(day.dayDate, cellDepartmentLocation)}
					leadingIcon={<AddIcon fontSize="small" />}
					data-testid="consecutive-master-schedule-add-shift"
					disabled={isMasterScheduleEditBlocked}
					tooltip={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : undefined}
				/>
			</Box>
		</Box>
	);
});

ConsecutiveCell.displayName = "ConsecutiveCell";
